<script setup lang="ts">
import AccordionComponent from "@/components/accordion/AccordionComponent.vue";
import { defineProps, inject, ref, defineModel, watch } from "vue";
import { DestinyMatrix, DestinyMatrixKeysEnum } from "@/types/destinyMatrix";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
  hasPremiumAccess: boolean;
}>();
const isOpen = defineModel<boolean>({ default: false });
const getDescription =
  inject<(key: DestinyMatrixKeysEnum) => Promise<never>>("getDescription");

const { t } = useI18n();

watch(
  () => props.matrix,
  () => (description.value = undefined)
);

const description = ref();

async function getData() {
  if (!props.hasPremiumAccess) {
    isOpen.value = false;
  }
  if (!description.value && props.matrix && getDescription) {
    description.value = await getDescription(
      DestinyMatrixKeysEnum.WHOM_WORKS_FOR_SOUL
    );
  }
}
</script>

<template>
  <AccordionComponent
    type="white"
    @opened="getData"
    v-model="isOpen"
    :isBlocked="!hasPremiumAccess"
  >
    <template #title>
      {{ t("calculators.destiny-matrix.accordions.whom-works-for-soul.title") }}
    </template>
    <template #faq-content>
      <div v-if="description" class="flex flex-col gap-2">
        <div class="whitespace-pre-line">
          {{
            t("calculators.destiny-matrix.accordions.whom-works-for-soul.desc")
          }}
        </div>
        <p class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.whom-works-for-soul.main-talent-title",
              { value: matrix.r1.l }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description[matrix.r1.l] }}
        </div>
        <p v-if="matrix.r1.l !== matrix.r1.t" class="text-xl font-bold pt-4">
          {{
            t(
              "calculators.destiny-matrix.accordions.whom-works-for-soul.middle-talent-title",
              { value: matrix.r1.t }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description[matrix.r1.t] }}
        </div>
        <p
          v-if="matrix.r1.l !== matrix.r1.r && matrix.r1.t !== matrix.r1.r"
          class="text-xl font-bold pt-4"
        >
          {{
            t(
              "calculators.destiny-matrix.accordions.whom-works-for-soul.end-talent-title",
              { value: matrix.r1.r }
            )
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ description[matrix.r1.r] }}
        </div>
      </div>
    </template>
    <template #collapse-text-btn>
      {{ t("calculators.destiny-matrix.accordions.collapse") }}
    </template>
  </AccordionComponent>
</template>
